/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 *
 * EOS Step 2 action service — provides change-detection functions,
 * workflow configuration validation, and session prolongation.
 *
 * Migrated from: app/view/app/eos/set-up/step2/eosSetUpStep2.action.service.js
 */

import { authApiService } from '../api/auth'
import { generalHelper } from '../generalHelper'
import { EOS_CONFIG_LEVEL, EOS_WORKFLOW_STEP_TYPE } from '../../constants/eos'
import type { EosConfig, EosOptions, EosUser, EosSite, EosCountrySite, EosWorkflowValidation } from '../../types/eos'

// ---------------------------------------------------------------------------
// hasChanges — returns a function that checks if the user made any selections
// ---------------------------------------------------------------------------

/**
 * Returns a function that, when called, checks whether any user/site selections
 * have changed from their original state (`wasSelected` vs `isSelected`).
 *
 * The returned function is designed to be called repeatedly (e.g., in a useEffect
 * or button-disable check) without re-creating the closure.
 */
export function hasChanges(config: EosConfig, options: EosOptions): () => boolean {
  switch (config.level) {
    case EOS_CONFIG_LEVEL.COUNTRY:
      return () => {
        const changedCountry = Object.values(options.countriesMap).find((country) => {
          return (country.sites ?? []).some((site) => isSiteChanged(site))
        })
        return !!changedCountry
      }

    case EOS_CONFIG_LEVEL.STUDY:
      return () => {
        const changedSite = Object.values(options.sitesMap).find((site) => {
          return (site.users ?? []).some((user) => isUserChanged(user as EosUser))
        })
        return !!changedSite
      }

    default:
      return () => false
  }
}

// ---------------------------------------------------------------------------
// getWorkflowConfigValidation — validates download/acknowledge combos per site
// ---------------------------------------------------------------------------

/**
 * Validates workflow configuration for each site.
 * Checks that download and acknowledge selections are consistent with
 * the current active workflow step.
 *
 * Returns an array of validation results (one per site in sitesMap).
 */
export function getWorkflowConfigValidation(config: EosConfig, options: EosOptions): EosWorkflowValidation[] {
  return Object.values(options.sitesMap).map((site) => {
    const wfStep = site.activeWf && site.activeWf.activeWorkflowStep ? site.activeWf.activeWorkflowStep : null

    const result = (site.users ?? []).reduce<EosWorkflowValidation>(
      (mem, user) => {
        const u = user as EosUser
        if (u.isAcknowledgeSelected) {
          mem.isAcknowledgeSelected = true
        }
        if (u.isDownloadSelected) {
          mem.isDownloadSelected = true
        }

        mem.isValid = (() => {
          switch (wfStep) {
            case EOS_WORKFLOW_STEP_TYPE.DOWNLOAD_EOS:
              return (
                (mem.isAcknowledgeSelected && mem.isDownloadSelected) ||
                Object.prototype.hasOwnProperty.call(site.activeWf, 'workflowId')
              )
            case EOS_WORKFLOW_STEP_TYPE.ACKNOWLEDGE_EOS:
              return mem.isAcknowledgeSelected || Object.prototype.hasOwnProperty.call(site.activeWf, 'workflowId')
            case null:
              return (
                (mem.isAcknowledgeSelected && mem.isDownloadSelected) ||
                (!mem.isAcknowledgeSelected && !mem.isDownloadSelected)
              )
            default:
              return mem.isValid
          }
        })()

        return mem
      },
      {
        site,
        isAcknowledgeSelected: false,
        isDownloadSelected: false,
        isValid: true
      }
    )

    return result
  })
}

// ---------------------------------------------------------------------------
// prolongUserSession — keeps the user session alive during long operations
// ---------------------------------------------------------------------------

/**
 * Keeps the user session alive by validating the auth token and dispatching
 * a synthetic mouse event. Called periodically during Step 2 to prevent
 * session timeout.
 */
export function prolongUserSession(): void {
  authApiService.validateToken()
  generalHelper.dispatchMouseEvent(0, 0)
}

// ---------------------------------------------------------------------------
// Private helpers
// ---------------------------------------------------------------------------

function isUserChanged(user: EosUser): boolean {
  return (
    user.isSelected !== user.wasSelected ||
    (!!user.isSelected &&
      (user.isAcknowledgeSelected !== user.wasAcknowledgeSelected ||
        user.isDownloadSelected !== user.wasDownloadSelected))
  )
}

function isSiteChanged(site: EosCountrySite): boolean {
  return site.isSelected !== site.wasSelected
}
